<template>
  <div>
    <!-- skeleton while searching -->
    <div
      v-if="!activeHeader"
      class="sourcery__container mt-16">
      <CollectionHeaderSkeleton />
      <CollectionsBaseSkeleton />
    </div>

    <!-- empty page -->
    <AppEmptyList
      v-else-if="isEmpty && activeHeader"
      class="pt-32"
      from="library-collections" />

    <!-- main content -->
    <template v-else>
      <!-- search bar -->
      <CollectionsSearch
        :is-store-product-preload="isStoreProductPreload"
        v-bind="propsDataForSearching" />

      <div
        v-show="!searchProductsMode"
        class="sourcery__container library-wrapper">
        <!-- title -->
        <CollectionHeader
          v-if="isLibraryToShow"
          :active-header="activeHeader"
          is-library />

        <!-- gallery/list view switch -->
        <div class="d-flex justify-space-between gap-4">
          <!-- left options -->
          <div>
            <AppSelect
              :default-item="selectedCollectionGroup"
              :items="filteredListofCollectionGroups()"
              @resort="selectedCollectionGroup = $event" />
          </div>

          <!-- right options -->
          <div class="d-flex gap-4">
            <AppSort
              :default-item="defaultItem"
              :sort-items="sortItems"
              @resort="fetchCollections" />

            <AppSwitcherToListing />
          </div>
        </div>

        <!-- grid view -->
        <component
          :is="isListingView ? 'CollectionListing' : 'CollectionsTable'"
          :collections-list="filteredList"
          :group="selectedCollectionGroup.group"
          @click-action-menu="onClickActionMenu({
            ...$event,
          })"
          @onIntersect="onIntersect({
            ...$event,
          })" />
      </div>
    </template>

    <CollectionCreateOrUpdate
      v-if="!searchProductsMode"
      v-on="$listeners" />

    <!-- sharing dialog -->
    <ShareAdvancedDialog
      :start-subscribe="true"
      :disable-actions="disableActionsSharing"
      :disabled="!(disableActionsSharing && !activeCollection.follow)"
      :invite-to-text-type="COLLECTION"
      :item="activeCollection"
      @manageSharedUser="
        manageSharedUser({
          variables: {
            ...$event.variables,
            collectionId: activeCollection.id,
          },
          criteria: $event.criteria,
        })
      " />

    <!-- 3 dots menu -->
    <CollectionsActions
      v-bind="actionsProps"
      :show-activator="false"
      :item.sync="selectedItem"
      :position-x="positionX"
      :position-y="positionY"
      :is-community-collections="isCommunityCollections"
      :value.sync="showActionsCard"
      @manageActions="manageActions" />
  </div>
</template>
<script>
import {
  mapState, mapActions, mapMutations, mapGetters,
} from 'vuex';

// components
import AppEmptyList from '@/components/App/AppEmptyList';
import AppSelect from '@/components/App/AppSelect';
import AppSwitcherToListing from '@/components/App/AppListingElements/AppSwitcherToListing';
import CollectionsActions from '@/components/Collections/CollectionsActions';
import CollectionsBaseSkeleton from '@/components/Collections/CollectionsBaseSkeleton';
import CollectionCreateOrUpdate from '@/components/CollectionsLibrarysRelocate';
import CollectionListing from '@/components/Listing/Collections';
import CollectionsSearch from '@/components/Collections/CollectionsSearch';
import CollectionsTable from '@/components/Collections/CollectionsTable';
import ShareAdvancedDialog from '@/components/CollectionsLibrarysRelocate/ShareAdvancedDialog';

// mixins
import AppActionDotsMenu from '@/mixins/AppActionDotsMenu';
import LoadingSpinnerReset from '@/mixins/LoadingSpinnerReset';
import ManageCollectionsActions from '@/mixins/ManageCollectionsActions';
import propsMyCollections from '@/mixins/propsMyCollections';
import PropsUtils from '@/mixins/PropsUtils';
import ToggleViewCondition from '@/mixins/ToggleViewCondition';

// utils
import {
  getFullLinkForWorkspaces, hasCreatorAccess, isOwner, hasCreatorAdminAccess,
} from '@/utils';

// constants
import {
  COMMUNITY_ID, ROUTE_COMMUNITY_COLLECTION,
} from '@/constants';
import { COLLECTION } from '@/constants/cores';
import { COMMENT_RESOURCE_TYPE } from '@/constants/comments';
import {
  NUMBER_OF_COLLAPSED_COLLECTION_IN_ROW,
  COLLECTIONS_WORKSPACE_GROUP,
  COLLECTIONS_PUBLISHED_GROUP,
  COLLECTIONS_FOLLOWED_GROUP,
  COMMUNITY_COLLECTIONS_GROUP,
} from '@/constants/collectionsList';
import {
  COLLAPSE_REORDER_SUBSCRIPTION, COLLECTION_ACCESS,
} from '@/constants/userPermissions';

// services
import SortingApi from '@/services/graphql/sorting';

export default {
  name: 'LibraryCollections',
  components: {
    AppEmptyList,
    AppSelect,
    AppSwitcherToListing,
    CollectionsActions,
    CollectionsBaseSkeleton,
    CollectionCreateOrUpdate,
    CollectionListing,
    CollectionsTable,
    CollectionsSearch,
    ShareAdvancedDialog,
    AppSort: () => import('@/components/App/AppSortV2'),
    CollectionHeader: () => import('@/components/Listing/CollectionHeader'),
    CollectionHeaderSkeleton: () => import('@/components/App/AppSkeleton/Collections/CollectionHeaderSkeleton'),
  },
  mixins: [
    AppActionDotsMenu,
    LoadingSpinnerReset,
    ManageCollectionsActions,
    propsMyCollections,
    PropsUtils,
    ToggleViewCondition,
  ],
  props: {
    expandedCollections: {
      type: Object,
      default: () => {
      },
    },
    hideColumnMembers: {
      type: Boolean,
      default: false,
    },
    isCommunityCollections: {
      type: Boolean,
      default: false,
    },
    isWorkspacePage: {
      type: Boolean,
      default: false,
    },
    isWorkspaceEditPage: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      COMMUNITY_ID,
      COLLECTION,
      actionsProps: {
      },
      selectedCollectionGroup: null,
      allCollections: null,
    };
  },
  computed: {
    ...mapState(['isMobile', 'isLoadingRowData']),
    ...mapState('Collections', ['activeCollection', 'searchProductsMode']),
    ...mapState('Libraries', ['librariesList']),
    ...mapState('Workspace', ['activeWorkspaceId']),
    ...mapGetters('Collections', ['getAppropriateCollectionsGroups']),
    ...mapGetters('Libraries', ['getCustomLibraryById']),
    ...mapGetters('FeatureFlags', [
      'mobileViewOptimizationsForTradeShow',
      'useMakePageFollowable',
      'useSkeleton',
      'useLazyLoading',
    ]),
    ...mapGetters('UserProfile', ['isSortedCollectionByLastAccessed']),
    ...mapGetters('UserRoles', [
      'canCreateCollectionInCustomLibrary',
      'canCreateCollectionInDefaultLibrary',
      'findUserRoleInLibrary',
    ]),
    ...mapGetters('Workspace', ['findWorkspacePageByPageId']),
    accessToken() {
      return this.$route?.query?.accessToken;
    },
    disableActionsSharing() {
      return ![ROUTE_COMMUNITY_COLLECTION, 'community-collections'].includes(
        this.$route.name
      );
    },
    filteredList() {
      const { filteredListOfCollections, selectedCollectionGroup, $store } = this;
      let responseArr = [];

      if (selectedCollectionGroup.value && selectedCollectionGroup.value === 'all') {
        filteredListOfCollections.map(group => {
          responseArr.push(
            ...$store.state.Collections[group.list],
          );
        });
      } else {
        responseArr.push(
          ...$store.state.Collections[selectedCollectionGroup.list],
        );
      }
      return responseArr;
    },
    getCreateCollectionRule() {
      if (this.isCustomLibrary) {
        return this.canCreateCollectionInCustomLibrary;
      }
      return this.canCreateCollectionInDefaultLibrary;
    },
    getLibraryById() {
      const { paramLibraryId: libraryId } = this;
      return this.$store.getters['Libraries/getLibraryById'](this.activeHeader?.libraryId || libraryId);
    },
    getLibraryName() {
      return this.getLibraryById.name;
    },
    getUserLibraryRole() {
      if (!this.activeHeader) return '';
      return this.findUserRoleInLibrary({
        ...this.activeHeader,
        checkIsPublishCollection: false,
      });
    },
    isAllowedCreateCollection() {
      const { mobileViewOptimizationsForTradeShow: flag } = this;
      if (!flag) return false;
      const { allowed = false } = this.getCreateCollectionRule(this.getUserLibraryRole) || {
      };
      return allowed;
    },
    isCustomLibrary() {
      return this.activeHeader?.kind == 'custom';
    },
    isEmpty() {
      if (this.isFetchingForAny) return false;

      for (const collList of this.filteredListOfCollections) {
        const listName = collList.list;
        const any = this.$store.state.Collections[listName].some(item => item && item.status !== 'deleted');
        if (any) return false;
      }

      return true;
    },
    isFetchingForAny() {
      if (!this.useLazyLoading) return false;
      const collFetchingState = this.$store.state.Collections.isFetching;

      if (collFetchingState.workspaceCollections
        || collFetchingState.publishedCollections
        || collFetchingState.followedCollections
        || collFetchingState.communityCollections) {
        return true;
      }

      const wsFetchingState = this.$store.state.Workspace.isFetching;
      if (wsFetchingState.workspacePages) return true;

      return false;
    },
    isListingView() {
      return this.toggleViewCondition('librariesRelatedPages');
    },
    paramLibraryId() {
      if (this.isWorkspacePage) {
        return COMMUNITY_ID;
      }
      return this.$route.params.id;
    },
    showAddIcon() {
      return !this.searchProductsMode && this.$route.name !== 'community-collections';
    },
    workspacePage() {
      const workspacePage = this.findWorkspacePageByPageId(this.$route.params.pageId);
      if (!workspacePage || !this.isWorkspacePage) {
        return {
          logo: '',
        };
      }
      return workspacePage;
    },
  },
  watch: {
    /**
     * @TODO refactor and leave in store
     * state for products mode
     * @param val
     */
    searchProductsMode(val) {
      this.setSearchProductsMode(val);
    },
    activeHeader: function(newAH, oldAH) {
      console.log('check delete this watcher');
      if (!oldAH && newAH) this.getAppropriateComponentInfo();
    },
  },
  created() {
    this.allCollections = {
      ...this.filteredListOfCollections[0],
      name: 'All Collections',
      value: 'all',
    };
    this.selectedCollectionGroup = this.allCollections;
  },
  async mounted() {
    this.getAppropriateComponentInfo();
    this.subscribeTotalUnreadCounter({
      resourceType: COMMENT_RESOURCE_TYPE.COLLECTION,
      libraryId: this.paramLibraryId,
    });
    this.subscribeCollectionsAccess();
  },
  beforeDestroy() {
    this.removeSubscriptions([COLLAPSE_REORDER_SUBSCRIPTION,
      `${COMMENT_RESOURCE_TYPE.COLLECTION}_unread_counter`, COLLECTION_ACCESS]);

    // cancel any pending requests
    this.filteredListOfCollections.map(el => {
      this.setIsCancelled({
        group: el.group,
        value: null,
      });
    });
  },
  methods: {
    ...mapActions({
      handleError: 'handleError',
      removeSubscriptions: 'ManageSubscriptions/removeSubscriptions',
      getCollectionsList: 'Collections/getCollectionsList',
      manageSharedUser: 'Collections/manageSharedUser',
      subscribeTotalUnreadCounter: 'Comments/subscribeTotalUnreadCounter',
      subscribeCollectionsAccess: 'Collections/subscribeCollectionsAccess',
      getCustomLibraryCollectionsList: 'Collections/getCustomLibraryCollectionsList',
      listWorkspacePages: 'Workspace/listWorkspacePages',
      onWorkspacePageChange: 'Workspace/onWorkspacePageChange',
    }),
    ...mapMutations({
      setUserData: 'UserProfile/setUserData',
      spinner: 'spinner',
      setIsLoadingRowData: 'setIsLoadingRowData',
      setIsCancelled: 'Collections/setIsCancelled',
      setSearchProductsMode: 'Collections/setSearchProductsMode',
      openInfoModal: 'openInfoModal',
    }),
    hasCreatorAccess,
    isOwner,
    hasCreatorAdminAccess,
    filteredListofCollectionGroups() {
      let groupNames = [
        this.allCollections,
        ...this.filteredListOfCollections,
      ];
      return groupNames;
    },
    getExpandedAmount({ expandedAmount = 0, rowsToShow = 0 } = {
    }) {
      if (expandedAmount) {
        return expandedAmount;
      }
      if (!rowsToShow) {
        return 0;
      }
      const { width } = this.$vuetify.breakpoint;
      if (width > 1110) {
        return NUMBER_OF_COLLAPSED_COLLECTION_IN_ROW.BIGGEST * rowsToShow;
      }
      if (width > 867) {
        return NUMBER_OF_COLLAPSED_COLLECTION_IN_ROW.MIDDLE * rowsToShow;
      }
      if (width > 500) {
        return NUMBER_OF_COLLAPSED_COLLECTION_IN_ROW.MOBILE_BIG * rowsToShow;
      }
      return NUMBER_OF_COLLAPSED_COLLECTION_IN_ROW.MOBILE_SMALL * rowsToShow;
    },
    async onClickActionMenu({ item, event }) {
      const collection = this.selectedCollectionGroup;
      const { group = null, actionTypes: actionsConditions = [], actions: showActions = false } = collection;
      const { kind = '' } = this.activeHeader;

      this.setMenuItem({
        item,
        event,
      });

      let finalActions = [...actionsConditions];
      if (group === 'wsPage' && !this.useMakePageFollowable) {
        finalActions = [];
      }
      this.actionsProps = {
        group,
        actionsConditions: finalActions,
        showActions,
        kind,
      };
    },
    async getAppropriateComponentInfo() {
      if (this.activeHeader) {
        await this.fetchCollections();
      }
    },
    getFullLinkForWorkspaces,
    filteredCollectionsList(list) {
      return this.$store.state.Collections[list].filter(item => item && item?.status !== 'deleted');
    },
    collectionListLength(collection) {
      return this.collectionList(collection).length;
    },
    showCollapsedItems({ group, showExpanded }) {
      return group
        && showExpanded
        && !this.expandedCollections[group];
    },
    collectionList({ list, group, expandedAmount = 0, rowsToShow = 0, showExpanded = false }) {
      if (this.isListingView) {
        return this.collectionListForListingView({
          list,
        });
      }
      return this.collectionListForTableView({
        list,
        group,
        showExpanded,
        expandedAmount: this.getExpandedAmount({
          expandedAmount,
          rowsToShow,
        }),
      });
    },
    collectionListForListingView({ list }) {
      return this.filteredCollectionsList(list);
    },
    collectionListForTableView({ list, group, expandedAmount = 0, showExpanded }) {
      const filtered = this.filteredCollectionsList(list);
      if (this.showCollapsedItems({
        group,
        showExpanded,
      })) {
        return [...filtered.slice(0, expandedAmount)];
      }
      return filtered;
    },
    collectionOptions(pagination) {
      return this.$store.state.Collections[pagination];
    },
    async fetchCollections(sortedParam) {
      this.setSpinner(true);
      if (this.useSkeleton) this.setIsLoadingRowData(true);

      if (sortedParam) {
        const { data } = await SortingApi.setSortingMode({
          resourceType: sortedParam.resourceType,
          sortingMode: sortedParam.sortingMode,
          ascending: sortedParam.ascending,
        });
        this.setUserData(data.response);
      }
      const { paramLibraryId: libraryId } = this;
      let pageId = null;
      if (this.isWorkspacePage) {
        pageId = this.$route.params?.pageId;
        await this.listWorkspacePages({
          accessToken: this.accessToken,
          pageId: this.$route.params?.pageId,
          workspaceId: this.$route.params?.workspaceId,
        });
      }
      if (this.activeHeader?.kind == 'default') {
        await Promise.all(this.filteredListOfCollections.map(async el => {
          await this.getCollectionsList({
            libraryId,
            collectionGroup: el.group,
            allowGetAllItems: el?.allowGetAllItems,
            pageId,
            isWsPage: this.isWorkspacePage,
            showSpinner: !this.useLazyLoading,
            filteredListOfCollection: this.filteredListOfCollections,
            ...(this.accessToken && {
              accessToken: this.accessToken,
            }),
            resetCancellation: true,
            comingFromRoute: this.$route.name,
            route: this.$route,
          });
        }));
      } else {
        if (sortedParam) {
          await this.getCustomLibraryCollectionsList({
            libraryId,
            workspaceId: this.activeWorkspaceId,
            route: this.$route,
          });
        }
      }
      if (this.useSkeleton) this.setIsLoadingRowData(false);
      this.setSpinner(false);
    },
    async onIntersect({
      entries,
      isIntersecting,
      expandedAmount = 0,
      rowsToShow = 0,
    }) {
      const {
        list: listName,
        collectionGroup,
        pagination,
      } = this.selectedCollectionGroup;

      await this.$nextTick();
      const nextToken = this.collectionOptions(pagination)?.nextToken;
      const { paramLibraryId: libraryId } = this;
      const list = this.collectionList({
        list: listName, group: collectionGroup, expandedAmount, rowsToShow,
      });
      if (isIntersecting) {
        const collectionId = entries[0].target.id;
        const lastCollectionId = list[list.length - 1].id;
        if (collectionId === lastCollectionId
          && nextToken
        ) {
          this.setSpinner(true);
          await this.getCollectionsList(
            {
              nextTokenForRequest: nextToken,
              collectionGroup,
              libraryId,
              pageId: this.$route.params?.pageId,
              isWsPage: this.isWorkspacePage,
              showSpinner: !this.useLazyLoading,
              ...(this.accessToken && {
                accessToken: this.accessToken,
              }),
              route: this.$route,
            });
          this.setSpinner(false);
        }
      }
    },
    isShowSection(section) {
      if (this.useLazyLoading) {
        if (this.isFetchingForGroup(section?.group)) return true;
      }
      return this.sectionHasCollectionsToShow(section);
    },
    sectionHasCollectionsToShow(section) {
      const lengthCollection = this.isListingView ? this.collectionListLength(section) : true;
      return this.checkTypeToShow(section.type) && !this.searchProductsMode && lengthCollection;
    },
    checkTypeToShow(type) {
      if (this.activeHeader) {
        const trueCondition = [this.activeHeader?.kind, 'any'];
        return trueCondition.includes(type);
      }
    },
    setSpinner(show) {
      if (!this.useSkeleton && !this.useLazyLoading) {
        this.spinner(show);
      }
    },
    isFetchingForGroup(collectionGroup) {
      if (!this.useLazyLoading) return false;
      const fetchingState = this.$store.state.Collections.isFetching;

      switch (collectionGroup) {
      case COLLECTIONS_WORKSPACE_GROUP:
        return fetchingState.workspaceCollections;
      case COLLECTIONS_PUBLISHED_GROUP:
        return fetchingState.publishedCollections;
      case COLLECTIONS_FOLLOWED_GROUP:
        return fetchingState.followedCollections;
      case COMMUNITY_COLLECTIONS_GROUP:
        return fetchingState.communityCollections;
      default:
        return false;
      }
    },
    isCollapseHeaderLoading(section) {
      if (section?.group == COLLECTIONS_WORKSPACE_GROUP) return false;
      if (this.sectionHasCollectionsToShow(section)) return false;

      return this.isFetchingForGroup(section?.group);
    },
  },
};
</script>
<style lang="scss" scoped>
.library-wrapper {
  display: flex;
  flex-direction: column;
  gap: 24px;
}
</style>